// Orcanos item History (change log) — parsing, tag discovery and diffing.
//
// Source of truth for the API contract:
//   ~/.claude/skills/orcanos-api/get-section-data/section-history.md
//
// The History section of QW_Get_Section_Data returns one row per change event.
// Each row is TWO levels deep:
//   field[]   — the event (change #, when, who, comment)
//   details[] — the per-field diffs, each its own field[] of
//               FieldName / OriginalValue / NewValue
//
// Tags (snapshots) are NOT a first-class API concept: the only place Orcanos
// exposes them is the event `Comment`. See `extractTags` below.

const ensureArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);

const flat = (fs) =>
  Object.fromEntries(ensureArray(fs).map((f) => [f.column, f.Value]));

// Event-shaped rows (Relations, Revision Downloaded, Description edits) record
// only THAT something happened — both sides read "Change Made". Rendering them
// literally produces "changed from Change Made to Change Made" noise.
const SENTINEL = 'Change Made';

// An unset value arrives as at least three different things, including one
// with a double space. Normalise before comparing or "blank → set" reads as a
// change from one word to another.
const UNSET = new Set(['', 'not set', '-- not set --', '--not set--']);

// ─── HTML values ─────────────────────────────────────────────────────────
//
// Rich-text fields log their raw markup as OriginalValue / NewValue. Compared
// literally, a save that only re-wrapped a paragraph or turned a space into
// &nbsp; reads as a change, and the modal would print tag soup. So an HTML
// value is reduced to what the reader actually sees — words, line breaks and
// images — both for comparison and for the word-level diff the modal renders.
//
// Pure string work (no DOM) so the Node smoke test can exercise it. The markup
// that finally reaches the page still goes through sanitizeHtml.

const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*\b[^>]*>/i;

/** True when a raw value carries HTML markup. */
export const isHtmlValue = (v) => HTML_TAG_RE.test(String(v ?? ''));

const ENTITIES = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

function decodeEntities(s) {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

const attrOf = (tag, name) => {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(tag);
  return m ? decodeEntities(m[1] ?? m[2] ?? m[3] ?? '') : '';
};

// An image's identity is its file, not its URL: the same picture can be stored
// as `///covarisFTP/x.png` in one save and `https://host/covarisFTP/x.png` in
// the next, and an access-token query must never make it look replaced.
const imageKey = (src) => src.split(/[?#]/)[0].split('/').filter(Boolean).pop() || src;

// Closing these starts a new line in the rendered field.
const BLOCK_END_RE = /^<\/(p|div|li|tr|h[1-6]|blockquote|pre)\b/i;

/**
 * Tokenise an HTML value into what a reader sees:
 *   { t: 'word', v }   { t: 'br' }   { t: 'img', src, alt, key }
 * Formatting tags (<b>, <span style>…) are dropped; line breaks are kept but
 * collapsed so an empty paragraph doesn't read as a change.
 */
export function htmlTokens(html) {
  const out = [];
  const pushBr = () => { if (out.length && out[out.length - 1].t !== 'br') out.push({ t: 'br' }); };
  const re = /<img\b[^>]*>|<br\s*\/?>|<[^>]*>|[^<]+/gi;
  let m;
  while ((m = re.exec(String(html ?? '')))) {
    const piece = m[0];
    if (/^<img\b/i.test(piece)) {
      const src = attrOf(piece, 'src');
      if (src) out.push({ t: 'img', src, alt: attrOf(piece, 'alt'), key: imageKey(src) });
    } else if (/^<br\b/i.test(piece) || BLOCK_END_RE.test(piece)) {
      pushBr();
    } else if (piece[0] !== '<') {
      for (const w of decodeEntities(piece).split(/\s+/)) if (w) out.push({ t: 'word', v: w });
    }
  }
  while (out.length && out[out.length - 1].t === 'br') out.pop();
  return out;
}

const tokenKey = (tok) => (tok.t === 'word' ? `w:${tok.v}` : tok.t === 'img' ? `i:${tok.key}` : 'br');

/** The readable text of an HTML value; images appear as `[image: file]`. */
export function htmlToText(html) {
  return htmlTokens(html)
    .map((tok) => (tok.t === 'word' ? tok.v : tok.t === 'img' ? `[image: ${tok.key}]` : '\n'))
    .join(' ')
    .replace(/ ?\n ?/g, '\n');
}

// Above this many LCS cells the word diff gives up and reports the differing
// middle as one removal + one addition — still correct, just coarser.
const DIFF_CELL_LIMIT = 4_000_000;

/**
 * Word-level diff of two HTML values → [{ op: 'eq'|'del'|'ins', tok }].
 * Common prefix/suffix are trimmed first, so a one-word edit in a long
 * description costs next to nothing.
 */
export function diffHtml(fromHtml, toHtml) {
  const a = isHtmlValue(fromHtml) ? htmlTokens(fromHtml) : textTokens(fromHtml);
  const b = isHtmlValue(toHtml) ? htmlTokens(toHtml) : textTokens(toHtml);

  let pre = 0;
  while (pre < a.length && pre < b.length && tokenKey(a[pre]) === tokenKey(b[pre])) pre++;
  let suf = 0;
  while (
    suf < a.length - pre && suf < b.length - pre &&
    tokenKey(a[a.length - 1 - suf]) === tokenKey(b[b.length - 1 - suf])
  ) suf++;

  const ma = a.slice(pre, a.length - suf);
  const mb = b.slice(pre, b.length - suf);
  const head = a.slice(0, pre).map((tok) => ({ op: 'eq', tok }));
  const tail = a.slice(a.length - suf).map((tok) => ({ op: 'eq', tok }));

  const n = ma.length, k = mb.length;
  if (n * k > DIFF_CELL_LIMIT) {
    return [
      ...head,
      ...ma.map((tok) => ({ op: 'del', tok })),
      ...mb.map((tok) => ({ op: 'ins', tok })),
      ...tail,
    ];
  }

  // Classic LCS table, walked forwards to emit the edit script.
  const ka = ma.map(tokenKey), kb = mb.map(tokenKey);
  const w = k + 1;
  const L = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = k - 1; j >= 0; j--) {
      L[i * w + j] = ka[i] === kb[j]
        ? L[(i + 1) * w + j + 1] + 1
        : Math.max(L[(i + 1) * w + j], L[i * w + j + 1]);
    }
  }
  const mid = [];
  let i = 0, j = 0;
  while (i < n && j < k) {
    if (ka[i] === kb[j]) { mid.push({ op: 'eq', tok: mb[j] }); i++; j++; }
    else if (L[(i + 1) * w + j] >= L[i * w + j + 1]) mid.push({ op: 'del', tok: ma[i++] });
    else mid.push({ op: 'ins', tok: mb[j++] });
  }
  while (i < n) mid.push({ op: 'del', tok: ma[i++] });
  while (j < k) mid.push({ op: 'ins', tok: mb[j++] });

  return [...head, ...mid, ...tail];
}

const textTokens = (v) =>
  normValue(v).split(/\s+/).filter(Boolean).map((w) => ({ t: 'word', v: w }));

/** Normalise a raw History value for COMPARISON (not for display). */
export function normValue(v) {
  const raw = isHtmlValue(v) ? htmlToText(v) : String(v ?? '');
  const s = raw.trim().replace(/\s+/g, ' ');
  return UNSET.has(s.toLowerCase()) ? '' : s;
}

/**
 * Normalise a raw History value for DISPLAY.
 * Multi-value fields come back pipe-delimited (`|DEV||EXECUTIVE|`), so split
 * on `|`, drop the empties and join readably. HTML is never pipe-split — a
 * `|` inside prose is just a character.
 */
export function displayValue(v) {
  if (isHtmlValue(v)) return normValue(v);
  const s = normValue(v);
  if (!s.includes('|')) return s;
  const parts = s.split('|').map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts.join(', ') : s;
}

/**
 * Parse a QW_Get_Section_Data(History) payload into events.
 *
 * Accepts either the full response envelope or its `Data` member; `Data` is
 * declared as a string in Swagger but arrives as an object on live instances,
 * so both are handled.
 *
 * Rows arrive newest-first (change # descending) — observed, not documented.
 * We sort explicitly on `changeNo` ASCENDING here, because every consumer
 * below walks the log forwards in time.
 */
export function parseHistory(payload) {
  const root = payload?.Data !== undefined ? payload.Data : payload;
  const d = typeof root === 'string' ? safeParse(root) : root;

  const events = ensureArray(d?.section?.history).map((row) => {
    const e = flat(row.field);
    const diffs = ensureArray(row.details).map((x) => flat(x.field));
    const real = diffs.filter(
      (x) => x.OriginalValue !== SENTINEL || x.NewValue !== SENTINEL
    );
    return {
      // ⚠️ The change-log SEQUENCE number, not the document revision letter.
      // Gaps in it are normal — never use it as a count or a dense index.
      changeNo: Number(e.Revision),
      version: e.obj_Version || '',
      when: e.TimeDone || '',
      who: e.UserChangedBy_name || '',
      comment: e.Comment || '',
      // The payload carries no before/after text for the description — this
      // flag is all there is. Surface it, never treat it as a diff source.
      descriptionChanged: e.descriptionInd === 'Yes',
      // No real diffs at all → the row logged an action, not a field change.
      isEvent: real.length === 0,
      // Orcanos logs every field TOUCHED by a save, not only those that
      // differ, so drop the no-ops too.
      changes: real
        .filter((x) => normValue(x.OriginalValue) !== normValue(x.NewValue))
        .map((x) => ({
          field: x.FieldName || '(unnamed field)',
          from: x.OriginalValue,
          to: x.NewValue,
        })),
    };
  });

  return events
    .filter((e) => Number.isFinite(e.changeNo))
    .sort((a, b) => a.changeNo - b.changeNo);
}

function safeParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// ─── Tags ────────────────────────────────────────────────────────────────

// [Item changed : ]Tag <Revision>.<Change> [Approved ]Created ( <KEY> )
const TAG_RE =
  /^(Item changed\s*:\s*)?Tag\s+(\S+?)\.(\d+)\s+(Approved\s+)?Created\s*\(\s*([^)]+?)\s*\)$/i;

/**
 * The tags (snapshots) of ONE item, newest first.
 *
 * Three traps, all of which inflate a tag list with things that aren't this
 * item's tags — each is handled below:
 *
 *  1. `Item changed : …` rows belong to a LINKED item — the key in parentheses
 *     is a different record. On one observed item only 4 of 21 tag-shaped rows
 *     were its own. Match the parenthesised key against the item queried.
 *  2. A tag usually produces TWO rows — `Created` and, a second later,
 *     `Approved Created`. They are one event: dedupe on (revision, change) and
 *     keep the Approved row, whose snapshot actually carries the approval.
 *  3. The `Approved` half is NOT guaranteed — a tag on an item that went to
 *     "In Work" appears as `Created` alone. Don't require the pair.
 *
 * `ownKeys` is the set of strings that identify this item (its Key, and its
 * numeric id). The parenthesised key is accepted when it matches one exactly
 * or when its numeric part matches the item id — Covaris keys are shaped
 * `PRT-39382-901631`, so an exact compare alone would be too strict.
 */
export function extractTags(events, ownKeys = []) {
  const keys = ownKeys.map((k) => String(k ?? '').trim().toLowerCase()).filter(Boolean);
  const ids = keys.map((k) => (k.match(/\d+/) || [''])[0]).filter(Boolean);

  const matchesItem = (key) => {
    const k = String(key || '').trim().toLowerCase();
    if (!k) return false;
    if (keys.includes(k)) return true;
    const num = (k.match(/\d+/) || [''])[0];
    return Boolean(num) && ids.includes(num);
  };

  const out = new Map();
  for (const ev of events) {
    const m = TAG_RE.exec((ev.comment || '').trim());
    if (!m) continue;
    if (m[1]) continue;                 // "Item changed :" → a LINKED item's tag
    if (!matchesItem(m[5])) continue;   // belt and braces

    const tag = `${m[2]}.${m[3]}`;
    const approved = Boolean(m[4]);
    const prev = out.get(tag);
    if (!prev || (approved && !prev.approved)) {
      out.set(tag, {
        tag,
        revision: m[2],
        change: Number(m[3]),
        approved,
        // This is what identifies the snapshot — both for diffing here and
        // for the generatehtmlpage snapshot URL.
        changeNo: ev.changeNo,
        when: ev.when,
        who: ev.who,
      });
    }
  }
  return [...out.values()].sort((a, b) => b.changeNo - a.changeNo);
}

/** The synthetic "Current" endpoint — everything after the newest tag. */
export const CURRENT = { id: '__current__', label: 'Current', changeNo: Infinity };

/** A stable select-option id for a tag. */
export const tagId = (t) => `tag:${t.tag}:${t.changeNo}`;

// ─── Diffing two points in the log ───────────────────────────────────────

/**
 * What changed between two points in the change log.
 *
 * A field can be touched several times inside the range — the user wants ONE
 * before/after per field, so we take the FIRST old value and the LAST new
 * value, exactly as a track-changes view of the whole span would read. A field
 * that was edited and then put back therefore drops out (first old === last
 * new), which is correct: nothing changed between the two snapshots.
 *
 * The range is (from, to] — the older endpoint's own event is excluded (it IS
 * the baseline) and the newer endpoint's is included. `to` may be CURRENT, in
 * which case everything after `from` counts.
 *
 * Returns { fields[], events[], descriptionChanged, from, to }.
 */
export function diffBetween(events, fromChangeNo, toChangeNo) {
  // Tolerate the endpoints being picked in either order.
  let lo = Math.min(fromChangeNo, toChangeNo);
  let hi = Math.max(fromChangeNo, toChangeNo);

  const inRange = events.filter((e) => e.changeNo > lo && e.changeNo <= hi);

  const byField = new Map();
  let descriptionChanged = false;

  for (const ev of inRange) {
    if (ev.descriptionChanged) descriptionChanged = true;
    for (const c of ev.changes) {
      const existing = byField.get(c.field);
      if (existing) {
        // Same field touched again — keep the original "before", advance "after".
        existing.to = c.to;
        existing.touches += 1;
        existing.lastWhen = ev.when;
        existing.lastWho = ev.who;
      } else {
        byField.set(c.field, {
          field: c.field,
          from: c.from,
          to: c.to,
          touches: 1,
          firstWhen: ev.when,
          firstWho: ev.who,
          lastWhen: ev.when,
          lastWho: ev.who,
        });
      }
    }
  }

  const fields = [...byField.values()]
    // Edited and reverted inside the span → no net change between the two points.
    .filter((f) => normValue(f.from) !== normValue(f.to))
    .map((f) => ({
      ...f,
      fromText: displayValue(f.from),
      toText: displayValue(f.to),
      // Rich text: the modal renders a word-level diff with images instead of
      // two struck/inserted strings.
      html: isHtmlValue(f.from) || isHtmlValue(f.to),
      added: normValue(f.from) === '',
      removed: normValue(f.to) === '',
    }))
    .sort((a, b) => a.field.localeCompare(b.field));

  return {
    fields,
    // Newest first for the "what happened" list under the diff.
    events: [...inRange].sort((a, b) => b.changeNo - a.changeNo),
    descriptionChanged,
    from: lo,
    to: hi,
  };
}
